import { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import { useI18n } from '../../utils/i18n';
import { useToast } from '../ToastProvider';

// #471 (Scheibe 1 aus #313): Filter-Einstellungen fuer Mails, Teams und
// Tickets -- dreimal dasselbe Muster aus Modal, Einstellungen und Ladezustand.
// Code unveraendert aus Dashboard.js uebernommen (Muster aus #312).
//
// manualRefreshEmails kommt als Funktion herein: Es stammt aus useAutoCache,
// das im Dashboard erst weiter unten aufgerufen wird. Gebraucht wird es erst
// beim Speichern des Mail-Filters, dann ist es da.
export default function useFilterSettings({ manualRefreshEmails }) {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const [showEmailFilterModal, setShowEmailFilterModal] = useState(false);
  const [emailFilterSettings, setEmailFilterSettings] = useState({});
  const [emailFilterLoading, setEmailFilterLoading] = useState(false);
  const [showTeamsFilterModal, setShowTeamsFilterModal] = useState(false);
  const [teamsFilterSettings, setTeamsFilterSettings] = useState({});
  const [teamsFilterLoading, setTeamsFilterLoading] = useState(false);
  const [showTicketFilterModal, setShowTicketFilterModal] = useState(false);
  const [ticketFilterSettings, setTicketFilterSettings] = useState({});
  const [ticketFilterLoading, setTicketFilterLoading] = useState(false);

  // Load email filter settings once on component mount
  useEffect(() => {
    const loadEmailFilterSettings = async () => {
      try {
        const response = await apiService.getEmailFilterSettings();
        console.log('🔍 [DASHBOARD] Email filter response:', response);
        
        if (response.success) {
          // Handle case where backend sends JSON as string
          let filterSettings = response.filterSettings;
          if (typeof filterSettings === 'string') {
            console.log('🔍 [DASHBOARD] Parsing filter settings from string');
            try {
              filterSettings = JSON.parse(filterSettings);
            } catch (error) {
              console.log('🚨 [DASHBOARD] Failed to parse filter settings, using defaults');
              filterSettings = { hideReadEmails: false, hoursFilter: 24, enableHoursFilter: false };
            }
          }
          
          const settingsStr = JSON.stringify(filterSettings);
          console.log('🔍 [DASHBOARD] Settings string length:', settingsStr.length);
          console.log('🔍 [DASHBOARD] Settings preview:', settingsStr.substring(0, 200));
          
          // Check if settings are corrupted and reset them
          if (settingsStr.length > 1000 || settingsStr.includes('"0":"')) {
            console.log('🚨 [DASHBOARD] Detected corrupted settings, resetting to defaults');
            setEmailFilterSettings({
              hideReadEmails: false,
              hoursFilter: 24,
              enableHoursFilter: false,
              keywords: response.keywords || ''
            });
          } else {
            setEmailFilterSettings({
              ...filterSettings,
              keywords: response.keywords
            });
          }
        }
      } catch (err) {
        console.error('Error loading email filter settings:', err);
      }
    };
    loadEmailFilterSettings();
  }, []);

  // Load teams filter settings once on component mount
  useEffect(() => {
    const loadTeamsFilterSettings = async () => {
      try {
        const response = await apiService.getTeamsFilterSettings();
        if (response.success) {
          setTeamsFilterSettings({
            ...response.filterSettings,
            keywords: response.keywords
          });
        }
      } catch (err) {
        console.error('Error loading teams filter settings:', err);
      }
    };
    loadTeamsFilterSettings();
  }, []);

  // Load ticket filter settings once on component mount
  useEffect(() => {
    const loadTicketFilterSettings = async () => {
      try {
        const response = await apiService.getTicketFilterSettings();
        if (response.success) {
          setTicketFilterSettings({
            ...response.filterSettings,
            keywords: response.keywords
          });
        }
      } catch (err) {
        console.error('Error loading ticket filter settings:', err);
      }
    };
    loadTicketFilterSettings();
  }, []);

  const handleEmailFilterSave = async (filterSettings) => {
    setEmailFilterLoading(true);
    try {
      const { keywords, ...settings } = filterSettings;
      await apiService.saveEmailFilterSettings(settings, keywords);
      setEmailFilterSettings(filterSettings);
      setShowEmailFilterModal(false);
      console.log('Email filter settings saved successfully');
      // The filtering will be applied automatically via useEffect
      // Sofortige Aktualisierung der E-Mail-Daten, damit neue Einstellungen direkt sichtbar sind
      try {
        await manualRefreshEmails();
      } catch (e) {
        console.warn('Manual email refresh after saving filter settings failed:', e?.message || e);
      }
    } catch (error) {
      console.error('Error saving email filter settings:', error);
      pushToast({ message: t('dashboard.saveEmailFilterError', 'Fehler beim Speichern der Filter-Einstellungen.'), type: 'error' });
    } finally {
      setEmailFilterLoading(false);
    }
  };

  const handleTeamsFilterSave = async (filterSettings) => {
    setTeamsFilterLoading(true);
    try {
      const { keywords, ...settings } = filterSettings;
      await apiService.saveTeamsFilterSettings(settings, keywords);
      setTeamsFilterSettings(filterSettings);
      setShowTeamsFilterModal(false);
      console.log('Teams filter settings saved successfully');
      // The filtering will be applied automatically via useEffect
    } catch (error) {
      console.error('Error saving teams filter settings:', error);
      pushToast({ message: t('dashboard.saveTeamsFilterError', 'Fehler beim Speichern der Teams-Filter-Einstellungen.'), type: 'error' });
    } finally {
      setTeamsFilterLoading(false);
    }
  };

  const handleTicketFilterSave = async (filterSettings) => {
    setTicketFilterLoading(true);
    try {
      const { keywords, ...settings } = filterSettings;
      await apiService.saveTicketFilterSettings(settings, keywords);
      setTicketFilterSettings(filterSettings);
      setShowTicketFilterModal(false);
      console.log('Ticket filter settings saved successfully');
      // The filtering will be applied automatically via useEffect
    } catch (error) {
      console.error('Error saving ticket filter settings:', error);
      pushToast({ message: t('dashboard.saveTicketFilterError', 'Fehler beim Speichern der Ticket-Filter-Einstellungen.'), type: 'error' });
    } finally {
      setTicketFilterLoading(false);
    }
  };

  return {
    showEmailFilterModal, setShowEmailFilterModal, emailFilterSettings, emailFilterLoading,
    showTeamsFilterModal, setShowTeamsFilterModal, teamsFilterSettings, teamsFilterLoading,
    showTicketFilterModal, setShowTicketFilterModal, ticketFilterSettings, ticketFilterLoading,
    handleEmailFilterSave, handleTeamsFilterSave, handleTicketFilterSave
  };
}
